import React, { useState, useEffect, useRef } from 'react';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import './validateTACPopup.css';
import axios from "../../../../src/services/httpService";
import getToken from "../../../store/api/api";
import { useSelector } from "react-redux";
import '../../../../src/pdfWorkerSetup';
// Configure PDF.js worker using CDN to avoid ES module issues

interface ValidateTACPopupProps {
  isOpen: boolean;
  onClose: () => void;
  fileUrl: string;
  tacVersion?: number;
  title?: string;
  tacTypeName?: string;
  tacVersionId?: string;
  tacEffectiveDate?: string;

}

const ValidateTACPopup: React.FC<ValidateTACPopupProps> = ({
  isOpen,
  onClose,
  fileUrl,
  tacVersion,
  title = "Terms and Conditions",
  tacTypeName,
  tacVersionId,
  tacEffectiveDate,


}) => {
  // State Variables:
  //- Terms and Conditions effective date: Date when pop up should display
  //- Terms and Conditions go live date: Date when pop up should record user acknowledgment (accept or close)
  // - When today is >= tacGoLiveDate date AND < tacEffectiveDate, showActiveTacDocument is set to true and pop-up is displayed.
  // - If user accepts the TAC,  userActionCompleted  is set to true, showActiveTacDocument is set to false and pop up will no longer be displayed.
  // - If user closes the pop up without accepting, showActiveTacDocument remains true and pop-up will be displayed again on next login.
  // - When today is >= tacEffectiveDate date, showActiveTacDocument is set to true, tacLogAcceptanceStatus is set to true and pop-up is not displayed.
  // - If user accepts the TAC,  userActionCompleted  is set to true, showActiveTacDocument is set to false and pop up will no longer be displayed.
  // - If user closes the pop up without accepting, showActiveTacDocument set to false, userActionCompleted  is set to true  and pop-up will no longer be displayed again on next login.

  const [isLoading, setIsLoading] = useState(true);
  const [textContent, setTextContent] = useState<string>('');
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string>('');
  const [fileType, setFileType] = useState<'text' | 'pdf'>('text');
  const [showFallbackOptions, setShowFallbackOptions] = useState(false);
  const [fetchError, setFetchError] = useState<string>('');
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pdfData, setPdfData] = useState<ArrayBuffer | null>(null);
  const [useIframe, setUseIframe] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const textDisplayRef = useRef<HTMLPreElement>(null);
  const pdfDisplayRef = useRef<HTMLIFrameElement>(null);

  const API_URL = axios.Api;
  const USERMANAGEAPI = axios.UserManageAPI || "";

  const userRegistrationDetails = useSelector((state: any) => state?.auth?.registrationDetails) || {};

  useEffect(() => {
    if (isOpen) {
      // Dashboard already handles ShowActiveTacDocument check, so proceed with fetching content
      fetchTextContent();

      // Disable context menu, text selection, and keyboard shortcuts on the document
      const disableInteractions = (e: Event) => {
        e.preventDefault();
        return false;
      };

      const disableKeyboard = (e: KeyboardEvent) => {
        // Disable common copy shortcuts
        if (
          (e.ctrlKey && (e.key === 'c' || e.key === 'a' || e.key === 's' || e.key === 'p')) ||
          (e.key === 'F12') ||
          (e.ctrlKey && e.shiftKey && e.key === 'I') ||
          (e.ctrlKey && e.shiftKey && e.key === 'J') ||
          (e.ctrlKey && e.key === 'u')
        ) {
          e.preventDefault();
          return false;
        }
      };

      document.addEventListener('contextmenu', disableInteractions);
      document.addEventListener('selectstart', disableInteractions);
      document.addEventListener('dragstart', disableInteractions);
      document.addEventListener('keydown', disableKeyboard);

      // Cleanup function
      return () => {
        document.removeEventListener('contextmenu', disableInteractions);
        document.removeEventListener('selectstart', disableInteractions);
        document.removeEventListener('dragstart', disableInteractions);
        document.removeEventListener('keydown', disableKeyboard);
      };
    }
  }, [isOpen, fileUrl, tacVersion]);

  // Reset accept button state when a new TAC is shown
  useEffect(() => {
    if (isOpen) {

      // Also reset scroll position when a new TAC opens
      setTimeout(() => {
        if (fileType === 'pdf' && pdfDisplayRef.current) {
          // For PDF iframe, try to scroll the content inside

          if (pdfDisplayRef.current.contentWindow && pdfDisplayRef.current.contentWindow.document) {
            pdfDisplayRef.current.contentWindow.document.documentElement.scrollTop = 0;
            // For PDF iframe, try to scroll the content inside
            try {
              pdfDisplayRef.current.contentWindow.document.body.scrollTop = 0;
            }
            catch (error) {
              console.log('Could not reset PDF scroll position:', error);
            }

          } else if (textDisplayRef.current) {
            textDisplayRef.current.scrollTop = 0;

          }
        }
      }, 800); // Longer delay for PDF loading
    }
  }, [isOpen, fileUrl, tacVersion, fileType]);

  const fetchTextContent = async () => {
    setIsLoading(true);
    setFetchError('');
    setShowFallbackOptions(false);

    // Clear previous content before fetching new document
    setTextContent('');
    setPdfBlobUrl('');
    setFileType('text');

    // Try API endpoint only
    try {
      const apiResponse = await fetchViaAPI();
      if (apiResponse.success && (apiResponse.content || apiResponse.blobUrl)) {
        if (apiResponse.fileType === 'pdf') {
          setFileType('pdf');
          setPdfBlobUrl(apiResponse.blobUrl!);
          // Set PDF data for react-pdf
          if (apiResponse.arrayBuffer) {
            setPdfData(apiResponse.arrayBuffer);
          }
          setUseIframe(false); // Try react-pdf first
        } else {
          setFileType('text');
          setTextContent(apiResponse.content!);
        }

        setIsLoading(false);

        // Reset scroll position to top for consecutive popups
        setTimeout(() => {
          if (apiResponse.fileType === 'pdf' && pdfDisplayRef.current) {
            // For PDF iframe, try to scroll the content inside
            try {
              if (pdfDisplayRef.current.contentWindow && pdfDisplayRef.current.contentWindow.document) {
                pdfDisplayRef.current.contentWindow.document.documentElement.scrollTop = 0;
                pdfDisplayRef.current.contentWindow.document.body.scrollTop = 0;
              }
            } catch (error) {
              console.log('Could not reset PDF scroll position:', error);
            }
          } else if (textDisplayRef.current) {
            textDisplayRef.current.scrollTop = 0;
          }
        }, 300); // Longer delay for PDF

        return;
      }
      // If API response has no content or failed
      throw new Error(apiResponse.error || 'No content received from API');
    } catch (error) {
      console.warn('API fetch failed:', error);
    }

    // API failed, show fallback options
    setIsLoading(false);
    setShowFallbackOptions(true);
    setFetchError('Unable to load document content through API.');
  };

  const fetchViaAPI = async () => {
    try {
      // Call your backend API endpoint
      const azureB2C = localStorage.getItem('AzureB2CImpersonate');
      let fixUrl = "";
      fixUrl = API_URL + USERMANAGEAPI + `User/GetFileFromCabinet`;

      // Detect file type from URL extension or default to text
      const isPdf = fileUrl.toLowerCase().includes('.pdf') || fileUrl.toLowerCase().includes('application/pdf');
      const fileType = isPdf ? 'pdf' : 'txt';

      const requestData = {
        url: fileUrl,
        type: fileType
      };

      const response = await fetch(fixUrl, {
        method: "Post",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + (await getToken()),
          ...(((azureB2C) && { UserImpersonated: azureB2C }) || {})
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      // Get content type from response headers
      const contentType = response.headers.get('content-type') || '';

      // Handle PDF files
      if (contentType.includes('application/pdf') || isPdf) {
        const arrayBuffer = await response.arrayBuffer();

        if (arrayBuffer.byteLength === 0) {
          throw new Error('PDF file is empty');
        }

        const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(blob);

        return {
          success: true,
          blobUrl: blobUrl,
          arrayBuffer: arrayBuffer, // For react-pdf
          fileType: 'pdf' as const,
          contentType: contentType
        };
      }
      // Handle text files
      else if (contentType.includes('text/') || contentType.includes('application/octet-stream') || !isPdf) {
        const content = await response.text();

        if (!content || content.trim().length === 0) {
          throw new Error('File content is empty');
        }

        return {
          success: true,
          content: content,
          fileType: 'text' as const,
          contentType: contentType
        };
      } else {
        // Handle JSON error response or unknown format
        try {
          const data = await response.json();
          if (data.IsSuccess === false) {
            throw new Error(data.Data || 'Failed to fetch content');
          }
          throw new Error('Unsupported response format');
        } catch (jsonError) {
          throw new Error('Unsupported response format');
        }
      }
    } catch (error) {
      console.error('API fetch error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'API fetch error occurred'
      };
    }
  };

  // React-PDF event handlers
  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);

    setIsLoading(false);

  };

  const onDocumentLoadError = (error: Error) => {
    console.error('PDF load error:', error);
    setUseIframe(true); // Fallback to iframe
    setIsLoading(false);
  };

  const handleScroll = () => {
    let scrollElement = null;
    let scrollTop = 0;
    let scrollHeight = 0;
    let clientHeight = 0;

    if (fileType === 'text' && textDisplayRef.current) {
      scrollElement = textDisplayRef.current;
      ({ scrollTop, scrollHeight, clientHeight } = scrollElement);
    } else if (fileType === 'pdf' && pdfDisplayRef.current) {
      // For PDFs, try to access iframe content but don't fail if blocked
      try {
        const iframe = pdfDisplayRef.current;
        if (iframe.contentWindow && iframe.contentWindow.document) {
          const pdfDocument = iframe.contentWindow.document;
          scrollTop = pdfDocument.documentElement.scrollTop || pdfDocument.body.scrollTop || 0;
          scrollHeight = pdfDocument.documentElement.scrollHeight || pdfDocument.body.scrollHeight || 0;
          clientHeight = pdfDocument.documentElement.clientHeight || pdfDocument.body.clientHeight || 0;
          scrollElement = iframe;
        } else {
          // Can't access PDF content, already handled by time-based approach
          return;
        }
      } catch (error) {
        // Cross-origin restriction - this is normal for PDF iframes
        // The time-based approach in handlePdfLoad will handle enabling the button
        return;
      }
    }

  };

  const updateTACStatus = (updates: { showActiveTacDocument?: boolean; userActionCompleted?: boolean; }) => {
    // Update localStorage to mark TAC as accepted
    const registrationDetails = JSON.parse(localStorage.getItem("registrationDetails") || "{}");

    if (registrationDetails.tacVersions && Array.isArray(registrationDetails.tacVersions)) {
      // Find the specific TAC version by tacVersionId and update it
      const tacToUpdate = registrationDetails.tacVersions.find((tac: any) =>
        tac.tacVersionId === tacVersionId || tac.tacVersion === tacVersion
      );

      if (tacToUpdate) {
        tacToUpdate.showActiveTacDocument = updates.showActiveTacDocument ?? false;
        if (updates.userActionCompleted !== undefined) {
          tacToUpdate.userActionCompleted = updates.userActionCompleted;
        }
      }
    }

    localStorage.setItem("registrationDetails", JSON.stringify(registrationDetails));
  };

  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return "N/A";

    try {
      // Parse date as UTC to avoid timezone conversion issues
      const date = new Date(dateString.split('T')[0] + 'T00:00:00');
      if (isNaN(date.getTime())) return "N/A";

      const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      };

      return date.toLocaleDateString('en-US', options);
    } catch (error) {
      return "N/A";
    }
  };

  const handleClose = async () => {

    setIsLoading(true);
    // Check if we should log acceptance to API
    let shouldLogAcceptance = false;
    const registrationDetails = JSON.parse(localStorage.getItem("registrationDetails") || "{}");
    if (registrationDetails.tacVersions && Array.isArray(registrationDetails.tacVersions)) {
      // Find the specific TAC version by tacVersionId and update it
      const tacToUpdate = registrationDetails.tacVersions.find((tac: any) =>
        tac.tacVersionId === tacVersionId || tac.tacVersion === tacVersion
      );

      if (tacToUpdate) {
        shouldLogAcceptance = tacToUpdate.tacLogAcceptanceStatus === true;
      }
    }

    // Check if document is not available
    const isDocumentNotAvailable = fetchError || showFallbackOptions || (!textContent && !pdfBlobUrl);

    // Only log acceptance if document is available and shouldLogAcceptance is true
    if (shouldLogAcceptance && !isDocumentNotAvailable) {
      try {
        // Call API to save acceptance info
        const azureB2C = localStorage.getItem('AzureB2CImpersonate');
        const apiUrl = API_URL + USERMANAGEAPI + `User/SaveUserTAC`;

        const acceptanceData = {
          tacVersion: tacVersion || 0,
          datebypassed: new Date().toISOString(),
          contactid: userRegistrationDetails?.customerId || 0,
          tcrAccepted: false
        };

        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + (await getToken()),
            ...(((azureB2C) && { UserImpersonated: azureB2C }) || {})
          },
          body: JSON.stringify(acceptanceData),
        });

        if (!response.ok) {
          console.error(`Failed to save acceptance: ${response.status}`);
        }

        updateTACStatus({ showActiveTacDocument: false, userActionCompleted: true });

      } catch (error) {
        console.error('Error saving TAC acceptance:', error);
      }
    }

    onClose();

  };

  const handleAccept = async (e?: React.MouseEvent<HTMLButtonElement>) => {
    // Prevent any default form submission or page reload
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }


    setIsLoading(true);

    try {
      // Call API to save acceptance info
      const azureB2C = localStorage.getItem('AzureB2CImpersonate');
      const apiUrl = API_URL + USERMANAGEAPI + `User/SaveUserTAC`;

      const acceptanceData = {
        tacVersion: tacVersion || 0,
        dateaccepted: new Date().toISOString(),
        contactid: userRegistrationDetails?.customerId || 0,
        tcrAccepted: true
      };

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + (await getToken()),
          ...(((azureB2C) && { UserImpersonated: azureB2C }) || {})
        },
        body: JSON.stringify(acceptanceData),
      });

      if (!response.ok) {
        throw new Error(`Failed to save acceptance: ${response.status}`);
      }

      updateTACStatus({ showActiveTacDocument: false, userActionCompleted: true });

    } catch (error) {
      console.error('Error saving TAC acceptance:', error);
      setFetchError('Failed to save acceptance. Please try again.');
      setIsLoading(false);
      return;
    }

    onClose();

  };

  // PDF scroll event handling
  const handlePdfLoad = () => {
    if (pdfDisplayRef.current) {



      // Try to detect scroll and only enable button when scrolled to bottom
      try {
        const iframe = pdfDisplayRef.current;

        if (iframe.contentWindow && iframe.contentWindow.document) {
          const pdfDocument = iframe.contentWindow.document;

          // Reset scroll position to top
          setTimeout(() => {
            try {
              pdfDocument.documentElement.scrollTop = 0;
              pdfDocument.body.scrollTop = 0;
            } catch (e) {
              console.log('Could not reset PDF scroll:', e);
            }
          }, 100);

          const pdfScrollHandler = () => {
            try {
              handleScroll();
            } catch (e) {
              console.log('PDF scroll handler error:', e);
            }
          };

          pdfDocument.addEventListener('scroll', pdfScrollHandler);

          // Check scroll state periodically
          const scrollCheckInterval = setInterval(() => {
            try {
              handleScroll();
            } catch (e) {
              clearInterval(scrollCheckInterval);
            }
          }, 1000);

          // Clear interval after 30 seconds
          setTimeout(() => {
            clearInterval(scrollCheckInterval);
          }, 30000);
        }
      } catch (error) {
        console.log('PDF iframe access blocked, using fallback timer:', error);

      }
    }
  };

  // Cleanup blob URLs when component unmounts or content changes
  useEffect(() => {
    return () => {
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
      }
    };
  }, [pdfBlobUrl]);


  // Additional useEffect to ensure scroll listener is added for new TACs
  useEffect(() => {
    if (isOpen && (textContent || pdfBlobUrl)) {
      const checkAndAddListener = () => {
        if (fileType === 'text') {
          const textDisplay = textDisplayRef.current;
          if (textDisplay) {
            // Ensure scroll listener is attached
            textDisplay.removeEventListener('scroll', handleScroll);
            textDisplay.addEventListener('scroll', handleScroll);
            handleScroll(); // Check initial state
          }
        } else if (fileType === 'pdf') {
          // PDF scroll handling will be managed in handlePdfLoad
          handleScroll(); // Check initial state
        }
      };

      setTimeout(checkAndAddListener, 300);
    }
  }, [isOpen, tacVersion, textContent, pdfBlobUrl, fileType]);


  if (!isOpen) return null;

  return (
    <div className="tac-popup-overlay">
      <div className="tac-popup-container">
        <div className="tac-popup-header">
          <h2>{title}</h2>
          <button
            className="tac-popup-close"
            onClick={handleClose}
            aria-label="Close popup"
          >
            ×
          </button>
        </div>

        <div className="tac-popup-content">
          {isLoading && (
            <div className="tac-loading">
              <div className="loading-spinner"></div>
              <p>Loading document...</p>
            </div>
          )}

          {(fetchError || showFallbackOptions || (!isLoading && !textContent && !pdfBlobUrl)) && (
            <div className="tac-error">
              <p>Current document is not available</p>
            </div>
          )}

          {!isLoading && fileType === 'text' && textContent && (
            <div className="tac-text-content">
              <pre ref={textDisplayRef} className="tac-text-display">{textContent}</pre>
            </div>
          )}

          {!isLoading && fileType === 'pdf' && pdfBlobUrl && (
            <div className="tac-pdf-content">
              {/* Use react-pdf if available and not using iframe fallback */}
              {!useIframe && pdfData ? (
                <div className="react-pdf-container">
                  <Document
                    file={pdfData}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={onDocumentLoadError}
                    loading={null}
                    error={<div className="pdf-error">Failed to load PDF</div>}
                    className="pdf-document"
                  >
                    <div className="pdf-page">
                      {Array.from(new Array(numPages), (_, index) => (
                        <Page
                          key={`page_${index + 1}`}
                          pageNumber={index + 1}
                          renderTextLayer={false}
                          renderAnnotationLayer={false}
                          scale={2}
                          loading={null}
                        />
                      ))}
                    </div>
                  </Document>

                </div>
              ) : (
                /* Fallback to iframe */
                <iframe
                  ref={pdfDisplayRef}
                  src={pdfBlobUrl}
                  className="tac-pdf-display"
                  onLoad={handlePdfLoad}
                  title="Terms and Conditions PDF"
                />
              )}
            </div>
          )}
        </div>

        {!isLoading && (textContent || pdfBlobUrl) && (
          <div className="tac-popup-footer"> <p className="tac-warning">
            We updated our Terms and Conditions – {tacTypeName || "N/A"}, effective {formatDate(tacEffectiveDate)}. These updates clarify certain terms and remove outdated references; they do not affect our service offerings or pricing. By clicking "Accept and Continue" you agree that (a) you have read the updated Terms and Conditions, (b) you are duly authorized to represent Customer (as defined in the Terms and Conditions), and (c) you accept the updated Terms and Conditions on behalf of Customer. If you have any questions, please contact your account representative.
          </p>

            <div className="tac-footer-actions">
              <button
                type="button"
                className="tac-accept-btn"

                onClick={handleAccept}
              >
                {'Accept and Continue'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ValidateTACPopup;
